import { __decorate, __metadata } from 'tslib';
import { trigger, transition, useAnimation } from '@angular/animations';
import * as i0 from '@angular/core';
import { inject, Renderer2, Injector, booleanAttribute, numberAttribute, Directive, Input, Component, ViewEncapsulation, ChangeDetectionStrategy, NgModule } from '@angular/core';
import { WindowRef, CoreModule } from '@odx/angular';
import { fadeIn, fadeOut } from '@odx/angular/animations';
import { CircularProgressComponent } from '@odx/angular/components/circular-progress';
import { CSSModifier, CSSComponent } from '@odx/angular/internal';
import { injectElement, px, deferFn } from '@odx/angular/utils';
import { DynamicViewService } from '@odx/angular/cdk/dynamic-view';

const LOADING_SPINNER_PARENT_CLASS = 'odx-loading-spinner-parent';
/**
 * A directive that controls the display of a loading spinner overlay on the host element.
 * This directive can dynamically create or remove a loading spinner based on the `isLoading` state.
 * It modifies the host element's styles to ensure the spinner is displayed correctly, and optionally,
 * it can set a minimum height to prevent layout shifts.
 */
class LoadingSpinnerDirective {
    constructor() {
        // private readonly disabledController = DisabledController.inject();
        // private readonly hasDisabledControllerOnHost = !!inject(DisabledController, { optional: true });
        this.dynamicViewService = inject(DynamicViewService);
        this.hostElement = injectElement();
        this.renderer = inject(Renderer2);
        this.injector = inject(Injector);
        this.windowRef = inject(WindowRef);
        this.viewRef = null;
        /**
         * Whether the spinner should automatically adjust its color based on the background.
         * Allows the user to specify if the spinner should use automatic color adjustment.
         *
         * @type {boolean}
         * @default false
         */
        this.autoColor = false;
    }
    /**
     * Controls whether the spinner is visible or not.
     * Manages the loading state, showing or hiding the spinner accordingly.
     *
     * @type {boolean}
     */
    set isLoading(value) {
        // TODO: Find out if something breaks by removing those lines
        // if (!this.hasDisabledControllerOnHost) {
        //   this.disabledController?.setDisabledState(value);
        // }
        if (value) {
            this.createSpinner();
        }
        else {
            this.removeSpinner();
        }
    }
    get isLoading() {
        return this.viewRef !== null;
    }
    /**
     * Sets the minimum height for the host element when the spinner is active, to avoid layout shifts.
     * Specifies the minimum height for the spinner's parent element.
     *
     * @type {number}
     */
    set minHeight(value) {
        this.parentMinHeight = value;
        this.isLoading && this.setParentHeight();
    }
    /**
     * Resets styles applied to the parent element when the spinner is removed.
     * Removes the class, minHeight, and position styles.
     */
    resetParentStyles() {
        this.renderer.removeClass(this.hostElement.nativeElement, LOADING_SPINNER_PARENT_CLASS);
        this.renderer.removeStyle(this.hostElement.nativeElement, 'minHeight');
        this.renderer.removeStyle(this.hostElement.nativeElement, 'position');
    }
    setParentHeight() {
        this.renderer.setStyle(this.hostElement.nativeElement, 'minHeight', this.parentMinHeight ? px(this.parentMinHeight) : 'inherit');
    }
    createSpinner() {
        this.renderer.addClass(this.hostElement.nativeElement, LOADING_SPINNER_PARENT_CLASS);
        this.setParentHeight();
        if (this.hasStaticPosition()) {
            this.renderer.setStyle(this.hostElement.nativeElement, 'position', 'relative');
        }
        this.viewRef = this.dynamicViewService.createView(LoadingSpinnerComponent, {
            injector: this.injector,
            context: { autoColor: this.autoColor },
            host: this.hostElement.nativeElement,
        });
        deferFn(() => (this.hostElement.nativeElement.scrollTop = 0));
    }
    removeSpinner() {
        this.viewRef?.destroy();
        this.viewRef = null;
    }
    hasStaticPosition() {
        return this.windowRef.nativeWindow.getComputedStyle(this.hostElement.nativeElement).position === 'static';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoadingSpinnerDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "16.1.0", version: "18.2.13", type: LoadingSpinnerDirective, isStandalone: true, selector: "[odxLoadingSpinner]", inputs: { autoColor: ["odxLoadingSpinnerAutoColor", "autoColor", booleanAttribute], isLoading: ["odxLoadingSpinner", "isLoading", booleanAttribute], minHeight: ["odxLoadingSpinnerMinHeight", "minHeight", numberAttribute] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoadingSpinnerDirective, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    selector: '[odxLoadingSpinner]',
                    // providers: [DisabledController.connect()],
                }]
        }], propDecorators: { autoColor: [{
                type: Input,
                args: [{ alias: 'odxLoadingSpinnerAutoColor', transform: booleanAttribute }]
            }], isLoading: [{
                type: Input,
                args: [{ alias: 'odxLoadingSpinner', transform: booleanAttribute }]
            }], minHeight: [{
                type: Input,
                args: [{ alias: 'odxLoadingSpinnerMinHeight', transform: numberAttribute }]
            }] } });

const BROWSER_TRANSPARENT_COLOR = 'rgba(0, 0, 0, 0)';
/**
 * Represents a loading spinner component.
 */
let LoadingSpinnerComponent = class LoadingSpinnerComponent {
    constructor() {
        this.host = inject(LoadingSpinnerDirective);
        this.windowRef = inject(WindowRef);
        this.backgroundColor = null;
        this.element = injectElement();
        /**
         * Indicates whether the color of the loading spinner should be automatically determined.
         * If set to true, the color will be based on the theme of the parent container.
         * If set to false, the color will be the default color specified in the component's styles.
         *
         * @type {boolean}
         * @default false
         */
        this.autoColor = false;
    }
    ngOnInit() {
        this.backgroundColor = this.resolveBackgroundColor(this.element.nativeElement);
    }
    animationFinished({ toState }) {
        if (toState === 'void') {
            this.host.resetParentStyles();
        }
    }
    resolveBackgroundColor(element) {
        const { backgroundColor } = this.windowRef.nativeWindow.getComputedStyle(element);
        if (backgroundColor !== BROWSER_TRANSPARENT_COLOR) {
            return backgroundColor;
        }
        if (element.parentElement) {
            return this.resolveBackgroundColor(element.parentElement);
        }
        return null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoadingSpinnerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: LoadingSpinnerComponent, isStandalone: true, selector: "odx-loading-spinner", inputs: { autoColor: "autoColor" }, host: { listeners: { "@hostAnimation.done": "animationFinished($event)" }, properties: { "@hostAnimation": "true" } }, ngImport: i0, template: "<odx-circular-progress size=\"auto\" value=\"-1\" />\n<div class=\"odx-loading-spinner__backdrop\" [style.backgroundColor]=\"backgroundColor\"></div>\n", dependencies: [{ kind: "component", type: CircularProgressComponent, selector: "odx-circular-progress", inputs: ["stroke", "size", "value"] }], animations: [trigger('hostAnimation', [transition(':enter', useAnimation(fadeIn())), transition(':leave', useAnimation(fadeOut()))])], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
__decorate([
    CSSModifier(),
    __metadata("design:type", Object)
], LoadingSpinnerComponent.prototype, "autoColor", void 0);
LoadingSpinnerComponent = __decorate([
    CSSComponent('loading-spinner')
], LoadingSpinnerComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoadingSpinnerComponent, decorators: [{
            type: Component,
            args: [{ standalone: true, selector: 'odx-loading-spinner', imports: [CircularProgressComponent], encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, host: {
                        '[@hostAnimation]': 'true',
                        '(@hostAnimation.done)': 'animationFinished($event)',
                    }, animations: [trigger('hostAnimation', [transition(':enter', useAnimation(fadeIn())), transition(':leave', useAnimation(fadeOut()))])], template: "<odx-circular-progress size=\"auto\" value=\"-1\" />\n<div class=\"odx-loading-spinner__backdrop\" [style.backgroundColor]=\"backgroundColor\"></div>\n" }]
        }], propDecorators: { autoColor: [{
                type: Input
            }] } });

const modules = [LoadingSpinnerComponent, LoadingSpinnerDirective];
class LoadingSpinnerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoadingSpinnerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: LoadingSpinnerModule, imports: [LoadingSpinnerComponent, LoadingSpinnerDirective], exports: [CoreModule, LoadingSpinnerComponent, LoadingSpinnerDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoadingSpinnerModule, imports: [LoadingSpinnerComponent, CoreModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoadingSpinnerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: modules,
                    exports: [CoreModule, ...modules],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { LoadingSpinnerComponent, LoadingSpinnerDirective, LoadingSpinnerModule };
